import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [bookForm, setBookForm] = useState({ name:'', email:'', phone:'', adults:2, children:0, date:'', requests:'' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`/api/tours/${slug}`).then(r => setTour(r.data)).catch(() => {}).finally(() => setLoading(false));
    window.scrollTo(0,0);
  }, [slug]);

  const handleBook = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('/api/bookings', { ...bookForm, tourId: tour._id, tourTitle: tour.title, totalAmount: tour.discountedPrice * (parseInt(bookForm.adults)||1) });
      setSuccess(true);
    } catch { } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={{ minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div className="spinner" />
    </div>
  );

  if (!tour) return (
    <div style={{ textAlign:'center', padding:'8rem 2rem' }}>
      <div style={{ fontSize:'4rem', marginBottom:'1rem' }}>🔍</div>
      <h2>Tour not found</h2>
      <Link to="/tours" className="btn btn-primary" style={{ marginTop:'1.5rem', display:'inline-flex' }}>Browse All Packages</Link>
    </div>
  );

  const saving = tour.price > tour.discountedPrice ? Math.round((1 - tour.discountedPrice/tour.price)*100) : 0;

  return (
    <div>
      {/* Hero */}
      <div style={{ position:'relative', height:480, overflow:'hidden' }}>
        <img src={tour.coverImage} alt={tour.title} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(180deg,rgba(16,37,66,0.30) 0%,rgba(16,37,66,0.75) 100%)' }} />
        <div className="container" style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', justifyContent:'flex-end', padding:'2.5rem clamp(1rem,4vw,2.5rem)', zIndex:1 }}>
          <div className="breadcrumb" style={{ justifyContent:'flex-start', marginBottom:'1rem' }}>
            <Link to="/">Home</Link><span className="breadcrumb-sep">›</span>
            <Link to="/tours">Packages</Link><span className="breadcrumb-sep">›</span>
            <span>{tour.title}</span>
          </div>
          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', marginBottom:'1rem' }}>
            <span className="badge badge-white">{tour.category === 'ladakh' ? '🏔️ Ladakh' : '🌿 Kashmir'}</span>
            <span className="badge badge-white" style={{ textTransform:'capitalize' }}>🏔️ {tour.difficulty}</span>
            {tour.isFeatured && <span className="badge badge-accent">⭐ Featured</span>}
          </div>
          <h1 style={{ color:'#fff', fontFamily:"'Playfair Display',serif", fontSize:'clamp(1.6rem,4vw,2.8rem)', lineHeight:1.2, maxWidth:700, marginBottom:'0.75rem' }}>{tour.title}</h1>
          <div style={{ display:'flex', gap:'1.5rem', flexWrap:'wrap', alignItems:'center' }}>
            <div style={{ display:'flex', gap:'0.35rem', alignItems:'center' }}>
              {Array(5).fill(0).map((_,i) => <span key={i} style={{ color: i < Math.floor(tour.rating) ? '#F59E0B' : 'rgba(255,255,255,0.35)', fontSize:'1rem' }}>★</span>)}
              <span style={{ color:'#fff', fontWeight:700, marginLeft:'0.3rem' }}>{tour.rating}</span>
              <span style={{ color:'rgba(255,255,255,0.75)', fontSize:'0.85rem' }}>({tour.reviewCount} reviews)</span>
            </div>
            <span style={{ color:'rgba(255,255,255,0.80)', fontSize:'0.9rem' }}>🕐 {tour.duration}</span>
            <span style={{ color:'rgba(255,255,255,0.80)', fontSize:'0.9rem' }}>👥 Max {tour.maxGroupSize} people</span>
            <span style={{ color:'rgba(255,255,255,0.80)', fontSize:'0.9rem' }}>📍 {tour.destinations?.join(', ')}</span>
          </div>
        </div>
      </div>

      <section className="section" style={{ paddingTop:'3rem', background:'var(--bg)' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:'2.5rem', alignItems:'start' }}>

            {/* Left Content */}
            <div>
              {/* Tabs */}
              <div className="tabs">
                {[['overview','Overview'],['itinerary','Day-wise Itinerary'],['inclusions','Inclusions & Exclusions'],['highlights','Highlights']].map(([key,lbl]) => (
                  <button key={key} className={`tab-btn${activeTab===key?' active':''}`} onClick={() => setActiveTab(key)}>{lbl}</button>
                ))}
              </div>

              {/* Overview */}
              {activeTab === 'overview' && (
                <div>
                  <h3 style={{ fontFamily:"'Playfair Display',serif", marginBottom:'1rem' }}>About This Tour</h3>
                  <p style={{ lineHeight:1.8, marginBottom:'2rem' }}>
                    Experience the magical beauty of Kashmir with our {tour.duration} package. Covering {tour.destinations?.join(', ')}, this journey takes you through some of the most breathtaking landscapes on Earth. From the shimmering Dal Lake to snow-capped mountains, every moment is crafted for unforgettable memories.
                  </p>

                  {/* Quick facts */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1rem', marginBottom:'2rem' }}>
                    {[
                      { icon:'🕐', label:'Duration', value: tour.duration },
                      { icon:'👥', label:'Group Size', value: `Max ${tour.maxGroupSize} people` },
                      { icon:'🏔️', label:'Difficulty', value: tour.difficulty },
                      { icon:'📍', label:'Destinations', value: tour.destinations?.length + ' places' },
                      { icon:'⭐', label:'Rating', value: `${tour.rating}/5 (${tour.reviewCount} reviews)` },
                      { icon:'🍽️', label:'Meals', value: 'Breakfast & Dinner' },
                    ].map((f,i) => (
                      <div key={i} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'1.1rem', display:'flex', alignItems:'center', gap:'0.8rem' }}>
                        <span style={{ fontSize:'1.4rem' }}>{f.icon}</span>
                        <div>
                          <div style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--muted)', marginBottom:'0.2rem' }}>{f.label}</div>
                          <div style={{ fontWeight:600, color:'var(--heading)', fontSize:'0.9rem' }}>{f.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Destinations */}
                  <h4 style={{ fontFamily:"'Playfair Display',serif", marginBottom:'0.85rem' }}>Destinations Covered</h4>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', marginBottom:'2rem' }}>
                    {tour.destinations?.map((d,i) => (
                      <span key={i} style={{ padding:'0.4rem 1rem', background:'var(--surface)', border:'1.5px solid var(--border)', borderRadius:100, fontSize:'0.88rem', fontWeight:600, color:'var(--heading)' }}>📍 {d}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Itinerary */}
              {activeTab === 'itinerary' && (
                <div>
                  <h3 style={{ fontFamily:"'Playfair Display',serif", marginBottom:'1.5rem' }}>Day-wise Itinerary</h3>
                  {tour.itinerary?.map((day,i) => (
                    <div key={i} className="itinerary-item">
                      <div className="itinerary-day">
                        <span style={{ fontSize:'0.62rem', letterSpacing:'0.05em' }}>DAY</span>
                        <span style={{ fontSize:'1rem' }}>{day.day}</span>
                      </div>
                      <div className="itinerary-content">
                        <h4 style={{ fontFamily:"'Playfair Display',serif", color:'var(--heading)', marginBottom:'0.4rem' }}>{day.title}</h4>
                        <p style={{ fontSize:'0.92rem', lineHeight:1.75, marginBottom:'0.75rem' }}>{day.description}</p>
                        {day.activities?.length > 0 && (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
                            {day.activities.map((a,j) => (
                              <span key={j} style={{ padding:'0.25rem 0.7rem', background:'rgba(58,175,169,0.10)', color:'var(--secondary-dark)', borderRadius:100, fontSize:'0.78rem', fontWeight:600, fontFamily:"'Poppins',sans-serif" }}>✦ {a}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Inclusions */}
              {activeTab === 'inclusions' && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2rem' }}>
                  <div>
                    <h4 style={{ fontFamily:"'Playfair Display',serif", color:'var(--success)', marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      ✓ What's Included
                    </h4>
                    <ul className="inc-list">
                      {tour.inclusions?.map((item,i) => (
                        <li key={i}><span className="inc-yes">✓</span>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 style={{ fontFamily:"'Playfair Display',serif", color:'var(--error)', marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      ✕ What's Not Included
                    </h4>
                    <ul className="inc-list">
                      {tour.exclusions?.map((item,i) => (
                        <li key={i}><span className="inc-no">✕</span>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Highlights */}
              {activeTab === 'highlights' && (
                <div>
                  <h3 style={{ fontFamily:"'Playfair Display',serif", marginBottom:'1.5rem' }}>Tour Highlights</h3>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                    {tour.highlights?.map((h,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem', padding:'1rem', background:'var(--surface)', borderRadius:'var(--radius-md)', border:'1px solid var(--border)' }}>
                        <span style={{ width:28, height:28, minWidth:28, borderRadius:'50%', background:'rgba(15,76,129,0.10)', color:'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', fontWeight:700 }}>{i+1}</span>
                        <span style={{ fontSize:'0.9rem', color:'var(--body)', lineHeight:1.6 }}>{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Booking Sidebar */}
            <div style={{ position:'sticky', top:'90px' }}>
              <div style={{ background:'var(--surface)', borderRadius:'var(--radius-lg)', padding:'2rem', boxShadow:'var(--shadow-lg)', border:'1px solid var(--border)' }}>
                {success ? (
                  <div style={{ textAlign:'center', padding:'2rem 0' }}>
                    <div style={{ fontSize:'3.5rem', marginBottom:'1rem' }}>🎉</div>
                    <h3 style={{ fontFamily:"'Playfair Display',serif", color:'var(--heading)', marginBottom:'0.5rem' }}>Booking Request Sent!</h3>
                    <p style={{ lineHeight:1.7, marginBottom:'1.5rem' }}>Our team will contact you within 2 hours to confirm your booking and provide all details.</p>
                    <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="btn btn-secondary w-full" style={{ justifyContent:'center' }}>💬 Chat on WhatsApp</a>
                  </div>
                ) : (
                  <>
                    {/* Price */}
                    <div style={{ borderBottom:'1px solid var(--border)', paddingBottom:'1.25rem', marginBottom:'1.5rem' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.5rem' }}>
                        {saving > 0 && <span className="price-saving">Save {saving}%</span>}
                        {tour.price > tour.discountedPrice && <span className="price-original">₹{tour.price?.toLocaleString('en-IN')}</span>}
                      </div>
                      <div style={{ display:'flex', alignItems:'baseline', gap:'0.4rem' }}>
                        <span style={{ fontFamily:"'Poppins',sans-serif", fontSize:'2rem', fontWeight:800, color:'var(--primary)' }}>₹{tour.discountedPrice?.toLocaleString('en-IN')}</span>
                        <span style={{ color:'var(--muted)', fontSize:'0.85rem' }}>per person</span>
                      </div>
                      <div style={{ display:'flex', gap:'0.4rem', marginTop:'0.6rem' }}>
                        {Array(5).fill(0).map((_,i) => <span key={i} style={{ color: i < Math.floor(tour.rating) ? '#F59E0B' : 'var(--border)' }}>★</span>)}
                        <span style={{ fontSize:'0.85rem', color:'var(--muted)', marginLeft:'0.3rem' }}>{tour.rating} ({tour.reviewCount})</span>
                      </div>
                    </div>

                    <form onSubmit={handleBook}>
                      <div style={{ marginBottom:'0.9rem' }}>
                        <label className="input-label">Full Name *</label>
                        <input className="input" required placeholder="Your name" value={bookForm.name} onChange={e => setBookForm(f => ({...f, name:e.target.value}))} />
                      </div>
                      <div style={{ marginBottom:'0.9rem' }}>
                        <label className="input-label">Email *</label>
                        <input className="input" type="email" required placeholder="your@email.com" value={bookForm.email} onChange={e => setBookForm(f => ({...f, email:e.target.value}))} />
                      </div>
                      <div style={{ marginBottom:'0.9rem' }}>
                        <label className="input-label">Phone *</label>
                        <input className="input" required placeholder="+91 98765 43210" value={bookForm.phone} onChange={e => setBookForm(f => ({...f, phone:e.target.value}))} />
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem', marginBottom:'0.9rem' }}>
                        <div>
                          <label className="input-label">Adults</label>
                          <input className="input" type="number" min="1" max="20" value={bookForm.adults} onChange={e => setBookForm(f => ({...f, adults:e.target.value}))} />
                        </div>
                        <div>
                          <label className="input-label">Children</label>
                          <input className="input" type="number" min="0" max="10" value={bookForm.children} onChange={e => setBookForm(f => ({...f, children:e.target.value}))} />
                        </div>
                      </div>
                      <div style={{ marginBottom:'0.9rem' }}>
                        <label className="input-label">Travel Date *</label>
                        <input className="input" type="date" required value={bookForm.date} onChange={e => setBookForm(f => ({...f, date:e.target.value}))} />
                      </div>
                      <div style={{ marginBottom:'1.5rem' }}>
                        <label className="input-label">Special Requests</label>
                        <textarea className="input" rows="2" placeholder="Dietary needs, room preferences, etc." value={bookForm.requests} onChange={e => setBookForm(f => ({...f, requests:e.target.value}))} style={{ minHeight:70 }} />
                      </div>

                      {/* Total */}
                      <div style={{ background:'rgba(15,76,129,0.05)', borderRadius:'var(--radius-md)', padding:'0.85rem 1rem', marginBottom:'1.25rem' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem', color:'var(--muted)' }}>
                          <span>₹{tour.discountedPrice?.toLocaleString('en-IN')} × {bookForm.adults||1} adults</span>
                          <span style={{ fontWeight:700, color:'var(--heading)' }}>₹{(tour.discountedPrice * (parseInt(bookForm.adults)||1)).toLocaleString('en-IN')}</span>
                        </div>
                      </div>

                      <button type="submit" className="btn btn-primary btn-lg w-full" style={{ justifyContent:'center' }} disabled={submitting}>
                        {submitting ? 'Sending...' : '✦ Request Booking'}
                      </button>
                    </form>

                    <div style={{ marginTop:'1rem', paddingTop:'1rem', borderTop:'1px solid var(--border)', display:'flex', gap:'1rem' }}>
                      <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem', padding:'0.65rem', background:'rgba(37,211,102,0.10)', color:'#128C3C', borderRadius:10, fontSize:'0.85rem', fontWeight:700 }}>
                        💬 WhatsApp
                      </a>
                      <a href="tel:+919876543210" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem', padding:'0.65rem', background:'rgba(15,76,129,0.08)', color:'var(--primary)', borderRadius:10, fontSize:'0.85rem', fontWeight:700 }}>
                        📞 Call Us
                      </a>
                    </div>
                    <p style={{ textAlign:'center', fontSize:'0.78rem', color:'var(--muted)', marginTop:'0.75rem' }}>Free cancellation up to 15 days before travel</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="whatsapp-fab">💬</a>

      <style>{`
        @media (max-width: 900px) {
          section .container > div[style*="grid-template-columns: 1fr 380px"] {
            grid-template-columns: 1fr !important;
          }
          div[style*="grid-template-columns: repeat(3,1fr)"] { grid-template-columns: 1fr 1fr !important; }
          div[style*="grid-template-columns: 1fr 1fr"][class=""] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
